import { useMemo } from "react";

import { useAuthStore, type Role } from "@/store/auth-store";

// Mirrors ``app.auth.roles.PERMISSIONS`` in the backend verbatim.
// IMPORTANT: every cell change here must also land in roles.py. The
// frontend matrix is a UX optimisation — the backend dependency is the
// authoritative gate.
const PERMISSIONS: Record<Role, ReadonlySet<string>> = {
  // A candidate sitting an assessment. ONE permission, on purpose --
  // this is an internet-facing system and the account belongs to a
  // stranger. Anything added here is handed to one.
  TESTRAD: new Set(["can_take_test"]),
  // Builds the exams and marks them. Deliberately NOT can_manage_users:
  // creating and expiring accounts is an administrative act.
  RADIOLOGIST: new Set(["can_manage_tests", "can_upload_test_cases"]),
  ADMIN: new Set([
    "can_manage_users",
    "can_manage_organisation",
    "can_manage_tests",
    "can_upload_test_cases",
  ]),
  SUPER_ADMIN: new Set([
    "can_manage_users",
    "can_manage_organisation",
    "can_take_test",
    "can_manage_tests",
    "can_upload_test_cases",
  ]),
};

// The role list, DERIVED rather than restated.
//
// There used to be a hand-written ``ALL_ROLES`` array in
// components/admin/lookups.ts. Being a plain array, it did not fail to
// compile when a role was added -- and its sibling ``ALL_PERMISSIONS``
// had in fact already drifted, silently missing can_manage_organisation.
// Deriving from the Record above makes TypeScript the enforcement: a new
// role cannot be added without filling in its cell, and the list follows
// for free.
export const ALL_ROLES = Object.keys(PERMISSIONS) as Role[];

export interface PermissionFlags {
  canManageUsers: boolean;
  canManageOrganisation: boolean;
  canTakeTest: boolean;
  canManageTests: boolean;
  canUploadTestCases: boolean;
}

export interface UsePermissionsResult extends PermissionFlags {
  role: Role | null;
  has: (permission: keyof PermissionFlags) => boolean;
}

const FLAG_TO_BACKEND: Record<keyof PermissionFlags, string> = {
  canManageUsers: "can_manage_users",
  canManageOrganisation: "can_manage_organisation",
  canTakeTest: "can_take_test",
  canManageTests: "can_manage_tests",
  canUploadTestCases: "can_upload_test_cases",
};

// Same reasoning as ALL_ROLES: derived, so it cannot drift.
export const ALL_PERMISSION_KEYS = Object.keys(
  FLAG_TO_BACKEND,
) as (keyof PermissionFlags)[];

export function usePermissions(): UsePermissionsResult {
  const role = useAuthStore((s) => s.user?.role ?? null);

  return useMemo<UsePermissionsResult>(() => {
    const grants = role ? PERMISSIONS[role] : null;
    const flag = (key: keyof PermissionFlags): boolean =>
      grants ? grants.has(FLAG_TO_BACKEND[key]) : false;

    return {
      role,
      canManageUsers: flag("canManageUsers"),
      canManageOrganisation: flag("canManageOrganisation"),
      canTakeTest: flag("canTakeTest"),
      canManageTests: flag("canManageTests"),
      canUploadTestCases: flag("canUploadTestCases"),
      has: flag,
    };
  }, [role]);
}
